import { Granit } from 'src/services/granits';
import { isAccesory } from 'src/utils/configurator.utils';
import { FaceDetails } from './useConfigurator.utils';

type PricingPieceInput = {
  polishingX: number;
  polishingY: number;
  polishingZ: number;
  sawingX: number;
  sawingY: number;
  sawingZ: number;
  importExtra: number;
  frenchExtra: number;
  granit: Granit;
  frenchLinearDetails: FaceDetails;
  frenchM2Details: FaceDetails;
  importLinearDetails: FaceDetails;
  importM2Details: FaceDetails;
  minPriceFrench?: number;
  minPriceImport?: number;
  name?: string;
  type: string;
};

type PricingResult = {
  ttc: number;
  ht: number;
  purchase: number;
};

const getValue = (value: number | undefined): number => {
  if (typeof value === 'number') return value;
  return 0;
};

/**
 * Calcule les extras (plus-values) linéaires et au m² pour une pièce.
 */
const computeExtras = (
  details: {
    linearDetails: FaceDetails;
    m2Details: FaceDetails;
    flatExtra: number;
  },
  x: number,
  y: number,
  z: number,
): number => {
  let extra = details.flatExtra;

  extra += getValue(details.linearDetails.faceX) * x;
  extra += getValue(details.linearDetails.faceX2) * x;
  extra += getValue(details.linearDetails.faceY) * y;
  extra += getValue(details.linearDetails.faceY2) * y;
  extra += getValue(details.linearDetails.faceZ) * z;
  extra += getValue(details.linearDetails.faceZ2) * z;

  extra += getValue(details.m2Details.faceX) * (z * y);
  extra += getValue(details.m2Details.faceX2) * (z * y);
  extra += getValue(details.m2Details.faceY) * (z * x);
  extra += getValue(details.m2Details.faceY2) * (z * x);
  extra += getValue(details.m2Details.faceZ) * (x * y);
  extra += getValue(details.m2Details.faceZ2) * (x * y);

  return extra;
};

/**
 * Calcule le prix d'une pièce (TTC, HT, prix d'achat).
 * Fonction pure : toutes les dépendances sont passées en paramètre.
 */
export const getPiecePrice = (
  activePiece: PricingPieceInput,
  x: number,
  y: number,
  z: number,
  assemblyUser: any,
  assemblyType: string | undefined,
  settings: { minPriceFrench?: number; minPriceImport?: number } | null,
  stock?: { price?: number },
): PricingResult => {
  // (((M3 monument * Prix au M3) + (Polissage * M2) + (sciage * M2)) * Coeff Admin France + plus values) * COEFF GRANIT CLIENT + (Transport à la Tonne * M3 * densité granit) * COEFF TRANSPORT CLIENT
  // + Tarif en euros accessoires * COEFF ACCESSOIRES CLIENT + (Transport à la Tonne * M3 * densité granit) * COEFF TRANSPORT CLIENT
  // + Nombre de lettres gravées * Tarif TTC à la lettre
  // + Tarif en euros Motif * Coeff MOTIF)
  // * Remise ou majoration (en % ou en euros sur le TTC) / si majoration, ventiler sur les différentes lignes

  let minFrenchPrice = settings?.minPriceFrench ?? 0;
  let minImportPrice = settings?.minPriceImport ?? 0;

  if (
    activePiece.minPriceFrench &&
    minFrenchPrice < activePiece.minPriceFrench
  ) {
    minFrenchPrice = activePiece.minPriceFrench;
  }
  if (
    activePiece.minPriceImport &&
    minImportPrice < activePiece.minPriceImport
  ) {
    minImportPrice = activePiece.minPriceImport;
  }

  const granitM3Price = activePiece.granit?.volumePrice ?? 0;
  const granitPurchaseM3Price = activePiece.granit?.purchaseVolumePrice ?? 0;
  const granitPolishingPrice = activePiece.granit?.polishingPrice ?? 0;
  const granitPurchasePolishingPrice =
    activePiece.granit?.purchasePolishingPrice ?? 0;
  const granitSawingPrice = activePiece.granit?.sawingPrice ?? 0;
  const granitPurchaseSawingPrice =
    activePiece.granit?.purchaseSawingPrice ?? 0;
  const adminCoeffImport = assemblyUser?.userCoefficients?.coeffImport ?? 1;
  const adminCoeffFr = assemblyUser?.userCoefficients?.coeffFr ?? 1;
  const adminCoeffEcoImport =
    assemblyUser?.userCoefficients?.coeffEcoImport ?? 1;
  const adminCoeffEcoFr = assemblyUser?.userCoefficients?.coeffEcoFr ?? 1;
  let deliveryPrice =
    Number.parseFloat(assemblyUser?.deliveryCost?.price ?? '0') ?? 0;
  const size = x * y * z;
  const TVA = 1.2;

  let ttc = 0;
  let ht = 0;

  if (assemblyUser?.quoteSettings?.postageDue) deliveryPrice = 0;
  let m2ToPolish = 0;
  if (activePiece.polishingX) m2ToPolish += z * y * activePiece.polishingX;
  if (activePiece.polishingY) m2ToPolish += x * z * activePiece.polishingY;
  if (activePiece.polishingZ) m2ToPolish += x * y * activePiece.polishingZ;

  let m2ToSaw = 0;
  if (activePiece.sawingX) m2ToSaw += z * y * activePiece.sawingX;
  if (activePiece.sawingY) m2ToSaw += x * z * activePiece.sawingY;
  if (activePiece.sawingZ) m2ToSaw += x * y * activePiece.sawingZ;

  let granitCoeff = 2.3;
  let monumentType: 'MONUMENT' | 'SOLE/PLATING' | 'COLUMBARIUM' | 'CINERARY';

  if (activePiece.type === 'SOLE' || activePiece.type === 'PLATING') {
    monumentType = 'SOLE/PLATING';
  } else {
    monumentType = (assemblyType as typeof monumentType) ?? 'MONUMENT';
  }
  if (assemblyUser?.userCoefficients?.isDetailed) {
    if (
      activePiece.granit?.fabrication === 'FRENCH' &&
      assemblyUser?.detailedCoefs[monumentType] &&
      assemblyUser?.detailedCoefs[monumentType][activePiece.granit.id]
    ) {
      granitCoeff =
        assemblyUser?.detailedCoefs[monumentType][activePiece.granit.id];
    } else if (
      activePiece.granit?.fabrication !== 'FRENCH' &&
      assemblyUser?.detailedCoefs[monumentType] &&
      assemblyUser?.detailedCoefs[monumentType][activePiece.granit.id]
    ) {
      granitCoeff =
        assemblyUser?.detailedCoefs[monumentType][activePiece.granit.id];
    }
  } else if (activePiece.granit?.fabrication === 'FRENCH') {
    const coeff = activePiece.granit.eco
      ? assemblyUser?.userCoefficients?.frenchEco ?? 2.3
      : assemblyUser?.userCoefficients?.frenchNormal ?? 2.3;
    granitCoeff = coeff ?? 2.3;
  } else {
    const coeff = activePiece.granit.eco
      ? assemblyUser?.userCoefficients?.importEco ?? 2.3
      : assemblyUser?.userCoefficients?.importNormal ?? 2.3;
    granitCoeff = coeff ?? 2.3;
  }

  let extra = 0;

  let largeExtraCoeff = 1;

  if (
    x * 100 > Number(activePiece.granit.largeSize) ||
    y * 100 > Number(activePiece.granit.largeSize) ||
    z * 100 > Number(activePiece.granit.largeSize)
  ) {
    largeExtraCoeff = Number(activePiece.granit.largeExtra);
  }

  if (activePiece.granit?.fabrication === 'FRENCH') {
    extra = computeExtras(
      {
        linearDetails: activePiece.frenchLinearDetails,
        m2Details: activePiece.frenchM2Details,
        flatExtra: activePiece.frenchExtra,
      },
      x,
      y,
      z,
    );
  } else {
    extra = computeExtras(
      {
        linearDetails: activePiece.importLinearDetails,
        m2Details: activePiece.importM2Details,
        flatExtra: activePiece.importExtra,
      },
      x,
      y,
      z,
    );
  }

  // pour le bon de commande on enleve toutes les plus values, tous le transport, tous les coeff,
  // donc il faut pour chaque granit: prix du m2 à polire en achat, à scier en achet et m3 achat
  // et ce sera un prux HT bon achat

  const purchasePrice =
    size * granitPurchaseM3Price +
    m2ToPolish * granitPurchasePolishingPrice +
    m2ToSaw * granitPurchaseSawingPrice;

  let userCoeff = granitCoeff;

  if (isAccesory(activePiece.type)) {
    userCoeff = assemblyUser?.userCoefficients?.accessories ?? 2.3;
  }

  if (activePiece.granit?.material === 'INOX') {
    userCoeff = assemblyUser?.userCoefficients?.pieceInox ?? 2.3;
  }

  if (activePiece.granit?.fabrication === 'FRENCH') {
    const adminCoeff = activePiece.granit.eco
      ? adminCoeffEcoFr
      : adminCoeffFr;

    let basePrice = 0;

    if (stock) {
      basePrice = stock.price ?? 0;
    } else {
      basePrice = Math.ceil(
        (size * (granitM3Price * largeExtraCoeff) +
          m2ToPolish * granitPolishingPrice +
          m2ToSaw * granitSawingPrice) *
          adminCoeff +
          extra,
      );

      if (basePrice < minFrenchPrice) {
        basePrice = minFrenchPrice;
      }
    }

    ht = basePrice;
    ttc = ht * userCoeff * TVA;
  } else {
    const adminCoeff = activePiece.granit.eco
      ? adminCoeffEcoImport
      : adminCoeffImport;
    let basePrice = 0;

    if (stock) {
      basePrice = stock.price ?? 0;
    } else {
      basePrice = Math.ceil(
        size * (granitM3Price * largeExtraCoeff) * adminCoeff,
      );

      if (basePrice < minImportPrice) {
        basePrice = minImportPrice;
      }

      basePrice = basePrice + extra;
    }

    ht = basePrice;
    ttc = ht * userCoeff * TVA;
  }

  return {
    ttc: Number.parseFloat(ttc.toFixed(2)),
    ht: Number.parseFloat(ht.toFixed(2)),
    purchase: purchasePrice,
  };
};

/**
 * Retourne le prix d'une gravure selon la taille.
 */
export const getPriceWithSize = (arrayOfPrice: any, size: number): number => {
  if (size === 1) return arrayOfPrice.coeff1;
  if (size === 2) return arrayOfPrice.coeff2;
  if (size === 3) return arrayOfPrice.coeff3;
  if (size === 4) return arrayOfPrice.coeff4;
  if (size === 5) return arrayOfPrice.coeff5;
  if (size === 6) return arrayOfPrice.coeff6;
  return arrayOfPrice.coeff7;
};
